import { useEffect, useState } from 'react';
import {
  CheckCircle2,
  ChevronDown,
  SlidersHorizontal,
  Sparkles,
  X,
} from 'lucide-react';

import ActionButton from '../../../../components/Buttons/ActionButton';
import Checkbox from '../../../../components/Forms/Input/Checkbox';
import TextAreaField from '../../../../components/Forms/Input/TextAreaField';

import { RestaurantModifierGroupDTO } from '../../../../types/restaurant/RestaurantCatalogExtensionTypes';
import { RestaurantMenuItemDTO } from '../../../../types/restaurant/RestaurantMenuTypes';
import { formatOrderMoney } from '../orderPresentation';

interface OrderModifierModalProps {
  item: RestaurantMenuItemDTO | null;
  groups: RestaurantModifierGroupDTO[];
  selectedIds: number[];
  notes: string;
  preview: number;
  valid: boolean;
  busy: boolean;
  onToggleModifier: (group: RestaurantModifierGroupDTO, id: number) => void;
  onNotesChange: (value: string) => void;
  onClose: () => void;
  onAdd: () => void;
}

const OrderModifierModal = ({
  item,
  groups,
  selectedIds,
  notes,
  preview,
  valid,
  busy,
  onToggleModifier,
  onNotesChange,
  onClose,
  onAdd,
}: OrderModifierModalProps) => {
  const [openGroups, setOpenGroups] = useState<Record<number, boolean>>({});

  // Al abrir el modal (nuevo plato o nuevas opciones): expandir solo los grupos
  // que aún requieren una selección; el resto queda plegado.
  useEffect(() => {
    if (!item) return;
    const next: Record<number, boolean> = {};
    groups.forEach((group, index) => {
      const selectedCount = group.modifiers.filter((modifier) =>
        selectedIds.includes(modifier.id)
      ).length;
      const satisfied =
        selectedCount >= group.minSelections &&
        selectedCount <= group.maxSelections &&
        (!group.required || selectedCount > 0);
      next[group.id] = index === 0 || group.required ? !satisfied : false;
    });
    setOpenGroups(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id, groups.length]);

  if (!item) return null;

  const toggleGroup = (groupId: number) =>
    setOpenGroups((current) => ({
      ...current,
      [groupId]: !current[groupId],
    }));

  return (
    <div className="fixed inset-0 z-99999 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
        <header className="flex items-start justify-between gap-4 border-b border-stroke bg-primary/5 p-5 dark:border-strokedark dark:bg-primary/10 sm:p-6">
          <div className="flex min-w-0 items-start gap-3">
            <span className="rounded-xl bg-primary p-2.5 text-white shadow-sm">
              <SlidersHorizontal className="h-5 w-5" />
            </span>

            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                Personalizar plato
              </p>
              <h2 className="mt-1 truncate text-xl font-bold text-black dark:text-white">
                {item.displayName || item.productName}
              </h2>
              <p className="mt-1 text-sm text-bodydark2 dark:text-bodydark">
                Elija las opciones disponibles antes de agregarlo a la orden.
              </p>
            </div>
          </div>

          <ActionButton
            label="Cerrar"
            title="Cerrar"
            icon={<X className="h-4 w-4" />}
            variant="secondary"
            size="sm"
            fullWidth={false}
            disabled={busy}
            onClick={onClose}
            className="shrink-0"
          />
        </header>

        <div className="space-y-3 p-5 sm:p-6">
          {groups.map((group) => {
            const selectedCount = group.modifiers.filter((modifier) =>
              selectedIds.includes(modifier.id)
            ).length;
            const groupValid =
              selectedCount >= group.minSelections &&
              selectedCount <= group.maxSelections &&
              (!group.required || selectedCount > 0);
            const open = openGroups[group.id] ?? false;
            const selectedNames = group.modifiers
              .filter((modifier) => selectedIds.includes(modifier.id))
              .map((modifier) => modifier.name)
              .join(', ');

            return (
              <section
                key={group.id}
                className={`overflow-hidden rounded-2xl border transition ${
                  groupValid
                    ? 'border-stroke dark:border-strokedark'
                    : 'border-danger/40 bg-danger/5'
                }`}
              >
                <button
                  type="button"
                  onClick={() => toggleGroup(group.id)}
                  aria-expanded={open}
                  className="flex w-full items-center justify-between gap-3 p-4 text-left transition hover:bg-gray-2/50 dark:hover:bg-meta-4/20 sm:px-5"
                >
                  <div className="min-w-0">
                    <h3 className="flex items-center gap-1.5 font-semibold text-black dark:text-white">
                      {group.name}
                      {group.required && (
                        <span className="text-danger">*</span>
                      )}
                    </h3>
                    <p
                      className={`truncate text-xs ${
                        groupValid ? 'text-bodydark2' : 'text-danger'
                      }`}
                    >
                      {!open && selectedNames
                        ? selectedNames
                        : `Seleccione entre ${group.minSelections} y ${group.maxSelections}`}
                    </p>
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    <span
                      className={`h-fit rounded-full px-2.5 py-1 text-xs font-semibold ${
                        groupValid
                          ? 'bg-success/10 text-success'
                          : 'bg-danger/10 text-danger'
                      }`}
                    >
                      {selectedCount}/{group.maxSelections}
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 text-bodydark2 transition-transform ${
                        open ? 'rotate-180' : ''
                      }`}
                    />
                  </div>
                </button>

                {open && (
                  <div className="grid gap-2 border-t border-stroke p-4 dark:border-strokedark sm:grid-cols-2 sm:px-5">
                    {group.modifiers
                      .filter((modifier) => modifier.active)
                      .map((modifier) => {
                        const selected = selectedIds.includes(modifier.id);

                        return (
                          <div
                            key={modifier.id}
                            className={`flex items-center gap-3 rounded-xl border p-3 transition ${
                              selected
                                ? 'border-primary/40 bg-primary/5 shadow-sm'
                                : 'border-stroke bg-white hover:border-primary/30 dark:border-strokedark dark:bg-boxdark-2'
                            }`}
                          >
                            <Checkbox
                              id={`order-modifier-${modifier.id}`}
                              checked={selected}
                              disabled={busy}
                              onChange={() =>
                                onToggleModifier(group, modifier.id)
                              }
                              className="shrink-0"
                            />

                            <label
                              htmlFor={`order-modifier-${modifier.id}`}
                              className="min-w-0 flex-1 cursor-pointer text-sm font-medium text-black dark:text-white"
                            >
                              {modifier.name}
                            </label>

                            <strong className="shrink-0 text-sm text-primary">
                              {modifier.priceAdjustment >= 0 ? '+' : ''}
                              {formatOrderMoney(modifier.priceAdjustment)}
                            </strong>
                          </div>
                        );
                      })}
                  </div>
                )}
              </section>
            );
          })}

          <TextAreaField
            label="Notas para cocina"
            name="orderModifierNotes"
            value={notes}
            rows={2}
            disabled={busy}
            placeholder="Ej. Sin cebolla, salsa aparte..."
            onChange={(event) => onNotesChange(event.target.value)}
          />

          <div className="flex items-center justify-between gap-4 rounded-xl border border-primary/20 bg-primary/5 p-4 dark:bg-primary/10">
            <span className="flex items-center gap-2 text-sm font-medium text-black dark:text-white">
              {valid ? (
                <CheckCircle2 className="h-5 w-5 text-success" />
              ) : (
                <Sparkles className="h-5 w-5 text-primary" />
              )}
              Total del plato
            </span>
            <strong className="text-lg text-primary">
              {formatOrderMoney(preview)}
            </strong>
          </div>
        </div>

        <footer className="flex flex-col-reverse gap-3 border-t border-stroke bg-gray-2/40 p-5 dark:border-strokedark dark:bg-meta-4/20 sm:flex-row sm:justify-end sm:px-6">
          <ActionButton
            label="Cancelar"
            variant="secondary"
            size="md"
            fullWidth={false}
            disabled={busy}
            onClick={onClose}
            className="w-full sm:w-auto"
          />

          <ActionButton
            label={`Agregar · ${formatOrderMoney(preview)}`}
            loading={busy}
            loadingLabel="Agregando..."
            variant="primary"
            size="md"
            fullWidth={false}
            disabled={!valid}
            onClick={onAdd}
            className="w-full sm:w-auto"
          />
        </footer>
      </div>
    </div>
  );
};

export default OrderModifierModal;
